<template>
  <a
    class="card"
    :class="[
      `card--${backgroundColor}`,
      {
        'card--sizes-panel-open': sizesPanelIsOpen,
        'is-selected': isCardSelected && isHoverStateEnabled,
      },
    ]"
    :href="productUrl"
    @click="handleClick"
    @mouseover="selectCard(true)"
    @mouseleave="selectCard(false)"
    v-data-test-id="'card'"
  >
    <CardVisualBox
      :currentVariant="currentVariant"
      :isShowingBackground="isShowingBackground"
      :backgroundColor="visualBackgroundColor"
      :badgeBackgroundColor="badgeBackgroundColor"
      :card="card"
      :productName="card.name"
      :imageFormat="imageFormat"
      :isGiftCardProduct="isGiftCardProduct"
      v-data-test-id="'card-visualbox'"
    />
    <div class="card__bottom-box" :class="{ 'card__bottom-box--all-gender': genderToShow === '' }">
      <transition name="fade" mode="out-in">
        <div
          v-if="isCardSelected && isHoverStateEnabled && !isMobile"
          class="card__variant-sliders"
        >
          <VariantSlider
            v-for="(slider, index) in sliders"
            :key="`slider-box-${index}`"
            :currentVariantIndex="currentVariantIndex"
            :currentSliderId="index"
            :isActive="currentSliderId === index"
            :isNavigationMode="true"
            :label="slider.label"
            :sliderId="index"
            :slidesPerPage="6"
            :variants="slider.variants"
            @slide-click="updateCurrentVariant"
            v-data-test-id="'variant-slider'"
          />
        </div>
        <CardDescription
          v-else
          :backgroundColor="descriptionBackgroundColor"
          :card="card"
          :isShowingBackground="isShowingBackground"
          :isGiftCardProduct="isGiftCardProduct"
          :productName="card.name"
          v-data-test-id="'card-description'"
        />
      </transition>
    </div>
  </a>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { getPathFromUrl } from '@/helpers/url.js'
import { GENDERS, PRODUCT_TYPES } from '@/stores/product'
import { BACKGROUND_COLORS as BADGE_BACKGROUND_COLORS } from '@/components/_shared/Badge/Badge.vue'
import CardVisualBox from '@/components/_shared/Cards/CardVisualBox.vue'
import CardDescription from '@/components/_shared/Cards/CardDescription.vue'
import { CONTENTFUL_IMAGE_FORMAT } from '@/components/_shared/ImageContentful.vue'
import VariantSlider from '@/components/_shared/VariantSliders/VariantSlider.vue'

export const CARD_COLORS = {
  light: 'light',
  ghost: 'ghost',
}

export const BACKGROUND_COLORS = {
  light: '#f7f7f7',
  ghost: 'rgba(255, 255, 255, 0.1)',
  transparent: 'transparent',
  white: '#fff',
}

const VARIANT_SLIDER_DELAY = 100
let timeout = ''

export default {
  name: 'Card',
  components: { CardVisualBox, CardDescription, VariantSlider },

  props: {
    backgroundColor: {
      type: String,
      validator(value) {
        return Object.values(CARD_COLORS).includes(value)
      },
      default: CARD_COLORS.light,
    },

    card: {
      type: Object,
      required: true,
    },

    context: {
      // Can be used to add context about where the card was used (e.g. 'search' - needed for GTM )
      type: String,
    },

    genderToShow: {
      type: String,
      default: '',
    },

    imageFormat: {
      type: String,
      default: CONTENTFUL_IMAGE_FORMAT.jpg,
    },

    index: {
      type: Number,
      default: 0,
    },

    isHoverStateEnabled: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      CONTENTFUL_IMAGE_FORMAT,
      currentSliderId: 0,
      currentVariantId: this.card.displayedVariantId,
      currentVariantIndex: 0,
      isCardSelected: false,
      isHover: false,
      mensVariants: [],
      unisexVariants: [],
      womensVariants: [],
      sizesPanelIsOpen: false,
    }
  },

  computed: {
    ...mapState('store', ['currency']),
    ...mapState('nav', ['currentAdditionalNav']),
    ...mapGetters({
      isMqLarge: 'viewport/isMqLarge',
    }),

    badgeBackgroundColor() {
      const matches = {
        [CARD_COLORS.light]: BADGE_BACKGROUND_COLORS.white,
        [CARD_COLORS.ghost]: BADGE_BACKGROUND_COLORS.white,
      }
      return matches[this.backgroundColor]
    },

    visualBackgroundColor() {
      const matches = {
        [CARD_COLORS.light]: BACKGROUND_COLORS.light,
        [CARD_COLORS.ghost]: BACKGROUND_COLORS.ghost,
      }
      return matches[this.backgroundColor]
    },

    descriptionBackgroundColor() {
      const matches = {
        [CARD_COLORS.light]: BACKGROUND_COLORS.white,
        [CARD_COLORS.ghost]: BACKGROUND_COLORS.transparent,
      }
      return matches[this.backgroundColor]
    },

    currentVariant() {
      const foundVariant = this.card.variants.find(variant => {
        return (
          variant.id === this.currentVariantId &&
          (this.isShowingAllGenders || variant.gender?.includes(this.genderToShow))
        )
      })
      const maybeFirstVariant = this.isShowingAllGenders
        ? this.card.variants[0]
        : this.card.variants.find(variant => variant.gender?.includes(this.genderToShow))
      return foundVariant || maybeFirstVariant || {}
    },

    currentSku() {
      return this.getCoreSku(this.currentVariant)
    },

    isGiftCardProduct() {
      return this.currentVariant?.spreeProduct?.productType === PRODUCT_TYPES.giftCards
    },

    isMobile() {
      return !this.isMqLarge
    },

    isShowingBackground() {
      return (
        this.isCardSelected &&
        !this.colorSelectorIsHovered &&
        !this.isMobile &&
        (this.backgroundImageUrl || this.backgroundVideoUrl)
      )
    },

    isShowingAllGenders() {
      return !this.genderToShow
    },

    productUrl() {
      let url = ''
      if (this.card.variants.length) {
        url = this.currentVariant.productUrl
        if (window.document.body.classList.contains('iframe-layout')) {
          const separator = url.indexOf('?') > -1 ? '&' : '?'
          url = url + separator + 'iframe=true'
        }
      }
      return url
    },

    includeMens() {
      return [GENDERS.mens, ''].includes(this.genderToShow) && this.mensVariants.length
    },

    includeWomens() {
      return [GENDERS.womens, ''].includes(this.genderToShow) && this.womensVariants.length
    },

    includeUnisex() {
      return this.unisexVariants.length
    },

    isInsideSearch() {
      return this.currentAdditionalNav === 'search'
    },

    sliders() {
      const sliders = []
      if (this.includeMens) sliders.push({ label: GENDERS.mens, variants: this.mensVariants })
      if (this.includeWomens) sliders.push({ label: GENDERS.womens, variants: this.womensVariants })
      if (this.includeUnisex) sliders.push({ label: GENDERS.unisex, variants: this.unisexVariants })
      return sliders
    },
  },

  watch: {
    currentSku: 'setCurrentVariantIndex',
  },

  created() {
    this.splitVariantsByGender()
  },

  mounted() {
    this.setCurrentVariantIndex(this.currentSku)
  },

  methods: {
    ...mapActions({
      showQuickViewOverlay: 'overlay/showQuickViewOverlay',
    }),

    ...mapMutations({
      setActiveCardVariant: 'nav/setActiveCardVariant',
    }),

    setCurrentVariantIndex(currentSku) {
      let index = []
      this.sliders.forEach(slider => {
        return index.push(slider.variants.findIndex(variant => Number(variant.sku) === currentSku))
      })
      this.currentVariantIndex = Math.max(...index) || 0
    },

    getCoreSku(variant) {
      return Number(variant.skus[0].sku.split(' ')[0])
    },

    splitVariantsByGender() {
      this.card.variants.forEach(variant => {
        const sku = this.getCoreSku(variant)
        if (variant.gender.length === 2) {
          this.unisexVariants.push({ sku, src: variant.imageUrl })
        } else if (variant.gender[0] === GENDERS.mens) {
          this.mensVariants.push({ sku, src: variant.imageUrl })
        } else this.womensVariants.push({ sku, src: variant.imageUrl })
      })
    },

    onSizesPanelChange(status) {
      this.sizesPanelIsOpen = status
    },

    handleClick(event) {
      const isProductPageVisit = [...event.target.classList].some(singleClass => {
        return ['card-visual-box__image-wrapper', 'card-visual-box__image'].includes(singleClass)
      })

      const isDirectNavigation =
        (isProductPageVisit && (!this.isMobile || this.isGiftCardProduct)) || this.isInsideSearch
      !isDirectNavigation ? event.preventDefault() : this.trackProductListClick()
      if (this.isMobile && !this.isInsideSearch && !this.isGiftCardProduct) {
        this.$emit('open-quickview')
        const productSlug = this.card.variants[0].spreeProduct.groupSlug
        this.showQuickViewOverlay({
          productSlug,
          genderToShow: this.genderToShow,
          sku: Number(this.currentVariant.spreeProduct.sku),
        })
        this.trackProductListView()
      }
    },

    selectCard(isSelected) {
      if (this.isInsideSearch || this.isCardSelected === isSelected) return
      clearTimeout(timeout)
      if (isSelected) timeout = setTimeout(() => (this.isCardSelected = true), VARIANT_SLIDER_DELAY)
      else this.isCardSelected = false
      this.setActiveCardVariant(this.currentVariant)
    },

    updateCurrentVariant(currentSku, currentIndex, currentSliderId) {
      this.currentVariantId = this.card.variants.find(
        variant => this.getCoreSku(variant) === currentSku
      ).id
      this.currentSliderId = currentSliderId
      this.trackProductListView()
    },

    /* For bigger screens - goes to PDP */
    trackProductListClick() {
      const productData = {
        items: {
          id: this.currentVariant?.spreeProduct?.sku,
          price: this.card.price,
          position: this.index + 1,
        },
        currency: document.body.dataset.currency,
        list: this.context || getPathFromUrl(window.location.href),
      }

      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.listClick,
        productData,
      })
    },

    /* For mobile - opens quickView / For desktop - on variant update */
    trackProductListView() {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.listView,
        productData: {
          viewmode: 'quickview',
          items: [
            {
              id: this.currentVariant?.spreeProduct?.sku,
              price: this.card.price,
              list: this.context || getPathFromUrl(window.location.href),
              position: this.index + 1,
            },
          ],
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import './Card';
</style>
